// Доступные языки
export type LanguageCode = 'en' | 'ru' | 'fr' | 'de' | 'es';

export interface Language {
  code: LanguageCode;
  name: string; // Название на родном языке
  flag: string; // Emoji флага
}

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

/**
 * Реальная структура объекта переводов после extractLanguageTranslations.
 * Ключи верхнего уровня — строки (из плоских dot-ключей JSON),
 * значения — строки или вложенные объекты.
 * Специальный namespace pf2e содержит PF2e controlled vocabulary из БД,
 * не из runtime JSON-файлов.
 */
export type TranslationNamespace = {
  pf2e?: Record<string, string | Record<string, string | Record<string, string>>>;
  [key: string]: string | Record<string, any> | undefined;
};

// Интерполяция параметров
export interface InterpolationParams {
  [key: string]: string | number;
}

// Тип для ключей перевода с поддержкой вложенности
export type TranslationKey<T extends object, Prefix extends string = ''> = {
  [K in keyof T]: T[K] extends object
  ? TranslationKey<T[K], `${Prefix}${K & string}.`>
  : `${Prefix}${K & string}`;
}[keyof T];

// Основной тип для функции перевода
export type TranslateFunction = (
  key: string,
  params?: InterpolationParams,
  defaultValue?: string
) => string;

// Интерфейс контекста локализации
export interface LocalizationContextType {
  /** Locale that is currently rendered. */
  language: LanguageCode;
  /** User preference, which may still be loading. */
  requestedLanguage: LanguageCode;
  preferredLanguage: LanguageCode;
  activeLanguage: LanguageCode | null;
  pendingLanguage: LanguageCode | null;
  setLanguage: (lang: LanguageCode) => void;
  reloadTranslations: () => Promise<void>;
  resetLocaleCache: () => Promise<void>;
  copyLocaleDiagnostics: () => Promise<boolean>;
  t: TranslateFunction;
  loadedLanguages: LanguageCode[];
  isLoading: boolean;
  isInitialLocaleReady: boolean;
  degradedFallback: null | 'previous_locale' | 'english_bundle' | 'bootstrap_bundle';
  error: string | null;
  version: string | null;
  cacheSource: 'memory_cache' | 'session_cache' | 'network' | 'english_bundle' | 'bootstrap_bundle' | null;
}

// Тип для хука useLocalization
export interface UseLocalizationReturn {
  language: LanguageCode;
  requestedLanguage: LanguageCode;
  preferredLanguage: LanguageCode;
  activeLanguage: LanguageCode | null;
  pendingLanguage: LanguageCode | null;
  setLanguage: (lang: LanguageCode) => void;
  reloadTranslations: () => Promise<void>;
  resetLocaleCache: () => Promise<void>;
  copyLocaleDiagnostics: () => Promise<boolean>;
  t: TranslateFunction;
  currentLanguage: Language | undefined;
  supportedLanguages: Language[];
  loadedLanguages: LanguageCode[];
  isLoading: boolean;
  isInitialLocaleReady: boolean;
  degradedFallback: null | 'previous_locale' | 'english_bundle' | 'bootstrap_bundle';
  error: string | null;
  version: string | null;
  cacheSource: 'memory_cache' | 'session_cache' | 'network' | 'english_bundle' | 'bootstrap_bundle' | null;
}
